import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bike, Car, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useVehicleCategories } from "../lib/systemConfig";
import { MotorcycleIcon } from "../components/icons/VehicleIcons";
import { Button } from "../components/ui/button";

const CATS = [
    { key: "motorrad", label: "Motorrad", desc: "Motorräder, Roller & Leichtkrafträder.", Icon: MotorcycleIcon },
    { key: "fahrrad", label: "Fahrrad", desc: "Fahrräder, E-Bikes & Pedelecs.", Icon: Bike },
    { key: "auto", label: "Auto", desc: "PKW / Automobile (Vorbereitung).", Icon: Car },
];

export default function AdminSystemCategoriesPage() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const { refresh } = useVehicleCategories();
    const [cats, setCats] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (loading) return;
        if (!user || user.role !== "admin") { navigate("/"); return; }
        api.get("/system/vehicle-categories")
            .then((r) => setCats({ motorrad: r.data?.motorrad !== false, fahrrad: !!r.data?.fahrrad, auto: !!r.data?.auto }))
            .catch(() => setCats({ motorrad: true, fahrrad: false, auto: false }));
    }, [user, loading, navigate]);

    const toggle = (k) => setCats((p) => ({ ...p, [k]: !p[k] }));

    const save = async () => {
        if (!cats.motorrad && !cats.fahrrad && !cats.auto) {
            toast.error("Mindestens eine Fahrzeug-Kategorie muss aktiv sein.");
            return;
        }
        setSaving(true);
        try {
            await api.put("/admin/system/vehicle-categories", cats);
            await refresh();
            toast.success("Fahrzeug-Kategorien gespeichert.");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen.");
        } finally {
            setSaving(false);
        }
    };

    if (loading || !cats) return <div className="p-12">Lädt…</div>;

    return (
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-8 md:py-12" data-testid="admin-system-categories-page">
            <div className="text-xs font-bold uppercase tracking-wider text-gray-500">Admin · System</div>
            <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight mb-2">Fahrzeug-Kategorien</h1>
            <p className="text-gray-600 mb-8 text-sm">
                Legt fest, welche Fahrzeug-Arten die Plattform anzeigt. Deaktivierte Kategorien werden überall
                ausgeblendet (Suche, Inserieren, Ankauf-Anfrage). Mindestens eine Kategorie muss aktiv bleiben.
            </p>

            <div className="space-y-3 mb-8">
                {CATS.map(({ key, label, desc, Icon }) => {
                    const on = !!cats[key];
                    return (
                        <button
                            key={key}
                            type="button"
                            onClick={() => toggle(key)}
                            className={`w-full flex items-center gap-4 p-4 border-2 rounded-sm text-left transition-colors ${on ? "border-[#111827] bg-[#FFCC00]/15" : "border-gray-200 hover:border-gray-400"}`}
                            data-testid={`cat-toggle-${key}`}
                        >
                            <Icon className="w-7 h-7 shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="font-bold">{label}</div>
                                <div className="text-xs text-gray-500">{desc}</div>
                            </div>
                            <span className={`inline-flex items-center h-6 px-2.5 rounded-full text-xs font-bold uppercase tracking-wider ${on ? "bg-emerald-100 text-emerald-800" : "bg-gray-100 text-gray-500"}`} data-testid={`cat-state-${key}`}>
                                {on ? "Aktiv" : "Aus"}
                            </span>
                        </button>
                    );
                })}
            </div>

            <Button onClick={save} disabled={saving} className="bg-[#111827] hover:bg-black text-white font-bold rounded-sm uppercase tracking-wider px-8" data-testid="save-categories-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Speichern</>}
            </Button>
        </div>
    );
}
